import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ImageUpload from '../ui/ImageUpload';
import { 
  Save, 
  Image as ImageIcon,
  Home,
  TreePine,
  MapPin,
  RefreshCw,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultImages = {
  hero_image: 'https://images.unsplash.com/photo-1586114539347-827507d2f006?w=1920&h=1080&fit=crop',
  about_image: 'https://images.unsplash.com/photo-1511884642898-4c92249e20b6?w=800&h=600&fit=crop',
  hunting_area_image: 'https://images.unsplash.com/photo-1534218238612-bace67b05bf2?w=800&h=500&fit=crop',
};

const AdminContent = () => {
  const [content, setContent] = useState({
    hero_image: '',
    about_image: '',
    hunting_area_image: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [useUpload, setUseUpload] = useState({
    hero_image: true,
    about_image: true,
    hunting_area_image: true,
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(prev => ({
        ...prev,
        hero_image: response.data.hero_image || defaultImages.hero_image,
        about_image: response.data.about_image || defaultImages.about_image,
        hunting_area_image: response.data.hunting_area_image || defaultImages.hunting_area_image,
      }));
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const items = Object.entries(content).map(([key, value]) => ({
        key,
        value,
        type: 'image'
      }));
      
      await axios.post(`${API}/content/batch`, { items });
      toast.success('Slike so bile shranjene');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Napaka pri shranjevanju');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (key) => {
    setContent(prev => ({
      ...prev,
      [key]: defaultImages[key]
    }));
  };

  const toggleInputMode = (key) => {
    setUseUpload(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const imageFields = [
    {
      key: 'hero_image',
      label: 'Glavna slika (Hero)',
      description: 'Slika na vrhu strani. Priporočena velikost: 1920x1080 px',
      icon: Home
    },
    {
      key: 'about_image',
      label: 'Slika "O nas"',
      description: 'Slika v sekciji O nas. Priporočena velikost: 800x600 px',
      icon: TreePine
    },
    {
      key: 'hunting_area_image',
      label: 'Slika lovišča',
      description: 'Slika v sekciji Lovišče. Priporočena velikost: 800x500 px',
      icon: MapPin
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ImageIcon className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Slike strani</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Shranjujem...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Shrani vse
            </span>
          )}
        </Button>
      </div>

      {/* Image Fields */}
      <div className="grid gap-6">
        {imageFields.map((field) => (
          <Card key={field.key} className="bg-stone-800/50 border-stone-700/50 p-6">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <field.icon className="w-5 h-5 text-emerald-400" />
                  <Label className="text-white font-semibold">{field.label}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleInputMode(field.key)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      useUpload[field.key] 
                        ? 'bg-emerald-600/20 text-emerald-400' 
                        : 'bg-stone-700 text-stone-400'
                    }`}
                  >
                    <Upload className="w-3 h-3 inline mr-1" />
                    Naloži
                  </button>
                  <button
                    onClick={() => toggleInputMode(field.key)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      !useUpload[field.key] 
                        ? 'bg-emerald-600/20 text-emerald-400' 
                        : 'bg-stone-700 text-stone-400'
                    }`}
                  >
                    URL
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-stone-500">{field.description}</p>

              <div className="flex gap-4">
                {/* Preview */}
                <div className="w-48 h-32 rounded-lg overflow-hidden bg-stone-900 flex-shrink-0">
                  {content[field.key] ? (
                    <img 
                      src={content[field.key]} 
                      alt={field.label}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = defaultImages[field.key];
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-600">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="flex-1">
                  {useUpload[field.key] ? (
                    <ImageUpload
                      value={content[field.key]}
                      onChange={(url) => setContent(prev => ({
                        ...prev,
                        [field.key]: url
                      }))}
                      previewClassName="hidden"
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={content[field.key]}
                          onChange={(e) => setContent(prev => ({
                            ...prev,
                            [field.key]: e.target.value
                          }))}
                          className="bg-stone-900/50 border-stone-700 text-white flex-1"
                          placeholder="https://example.com/slika.jpg"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReset(field.key)}
                          className="border-stone-700 text-stone-400 hover:bg-stone-800"
                          title="Ponastavi na privzeto"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Info */}
      <Card className="bg-emerald-900/20 border-emerald-600/30 p-4">
        <p className="text-sm text-emerald-300">
          <strong>Novo:</strong> Slike lahko sedaj naložite neposredno! 
          Kliknite gumb "Naloži" in izberite sliko iz računalnika ali jo povlecite v polje.
        </p>
      </Card>
    </div>
  );
};

export default AdminContent;
